"use client"

import Link from "next/link"
import { useParams, usePathname } from "next/navigation"
import {
  LayoutDashboard,
  FileText,
  Users,
  Shield,
  PlusCircle,
  TableProperties,
  Calculator,
  SlidersHorizontal,
  BadgePlus,
  GitBranch,
  History,
  ClipboardList,
} from "lucide-react"
import { usePermissions } from "@/hooks/use-permissions"
import { ROUTES } from "@/lib/routes"
import type {
  PermissionModule,
  PermissionAction,
} from "@/hooks/use-permissions"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { NovaLogo } from "@/public/images/NovaLogo"
import { SalamaLogo } from "@/public/images/SalamaLogo"

interface NavItem {
  label: string
  href: (locale: string) => string
  icon: React.ElementType
  permission: { module: PermissionModule; action: PermissionAction } | null
}

interface NavSection {
  title: string
  items: NavItem[]
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: "Overview",
    items: [
      {
        label: "Dashboard",
        href: (locale) => ROUTES.dashboard(locale),
        icon: LayoutDashboard,
        permission: { module: "dashboard", action: "view" },
      },
    ],
  },
  {
    title: "Quotes",
    items: [
      {
        label: "Generate Quote",
        href: (locale) => ROUTES.plans(locale),
        icon: PlusCircle,
        permission: null,
      },
      {
        label: "All Quotes",
        href: (locale) => ROUTES.quotesHistory(locale),
        icon: FileText,
        permission: { module: "quotes", action: "view" },
      },
    ],
  },

  // {
  //   title: "Pricing",
  //   items: [
  //     {
  //       label: "Top-ups",
  //       href: (locale) => ROUTES.dashboardPricing(locale),
  //       icon: DollarSign,
  //       permission: { module: "pricing", action: "view" },
  //     },
  //     {
  //       label: "Loadings",
  //       href: (locale) => ROUTES.dashboardPricingLoadings(locale),
  //       icon: Percent,
  //       permission: { module: "pricing", action: "view" },
  //     },
  //   ],
  // },
  {
    title: "Plans And Pricing",
    items: [
      {
        label: "Plans List",
        href: (locale) => ROUTES.dashboardPlans(locale),
        icon: ClipboardList,
        permission: { module: "plans", action: "view" },
      },
      {
        label: "Plan Tiers",
        href: (locale) => ROUTES.dashboardPricingPlanTiers(locale),
        icon: TableProperties,
        permission: { module: "admin-plan-tiers", action: "view" },
      },
      {
        label: "Plan Tier Config",
        href: (locale) => ROUTES.dashboardPricingPlanTierConfig(locale),
        icon: FileText,
        permission: { module: "admin-benefit-sections", action: "manage" },
      },
      {
        label: "Rate Tables",
        href: (locale) => ROUTES.dashboardPricingRates(locale),
        icon: Calculator,
        permission: { module: "admin-base-rates", action: "view" },
      },
      {
        label: "Loading Configuration",
        href: (locale) => ROUTES.dashboardPricingConfiguration(locale),
        icon: SlidersHorizontal,
        permission: { module: "admin-loading-config", action: "view" },
      },
      {
        label: "Optional Benefits",
        href: (locale) => ROUTES.dashboardPricingOptionalBenefits(locale),
        icon: BadgePlus,
        permission: { module: "admin-optional-benefits", action: "view" },
      },
      {
        label: "Adjustments",
        href: (locale) => ROUTES.dashboardPricingAdjustments(locale),
        icon: GitBranch,
        permission: { module: "admin-adjustment-factors", action: "view" },
      },
      {
        label: "Version History",
        href: (locale) => ROUTES.dashboardPricingVersions(locale),
        icon: History,
        permission: { module: "admin-adjustment-factors", action: "view" },
      },
    ],
  },
  {
    title: "Administration",
    items: [
      {
        label: "Users",
        href: (locale) => ROUTES.dashboardUsers(locale),
        icon: Users,
        permission: { module: "users", action: "view" },
      },
      {
        label: "Roles & Permissions",
        href: (locale) => ROUTES.dashboardRoles(locale),
        icon: Shield,
        permission: { module: "roles", action: "view" },
      },
    ],
  },
]

export function AppSidebar() {
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  const pathname = usePathname()
  const { hasPermission } = usePermissions()

  function isActive(href: string): boolean {
    const hrefClean = href.replace(/\/$/, "")
    const pathnameClean = pathname.replace(/\/$/, "")
    // Exact match for root paths (e.g. dashboard, users) to avoid over-highlighting
    // when a child route (e.g. /users/roles) is active
    if (
      hrefClean.endsWith("/dashboard") ||
      hrefClean === `/${locale}` ||
      hrefClean === `/${locale}/dashboard` ||
      hrefClean === ROUTES.dashboardUsers(locale)
    ) {
      return pathnameClean === hrefClean
    }
    // For section routes, match if the current path starts with the nav href
    return (
      pathnameClean === hrefClean || pathnameClean.startsWith(hrefClean + "/")
    )
  }

  // Filter sections based on permissions
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (!item.permission) return true
      return hasPermission(item.permission.module, item.permission.action)
    }),
  })).filter((section) => section.items.length > 0)

  return (
    <Sidebar className="bg-sidebar" collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-2">
          <NovaLogo className="h-9 w-auto text-sidebar-foreground dark:hidden" />
          <NovaLogo
            variant="white"
            className="hidden h-9 w-auto opacity-90 dark:block"
          />
          <div className="ml-1 flex h-8 items-center">
            <SalamaLogo className="h-6 w-auto object-contain dark:hidden" />
            <SalamaLogo
              variant="white"
              className="hidden h-6 w-auto object-contain opacity-90 dark:block"
            />
          </div>
        </div>
      </SidebarHeader>

      {/* Nav */}
      <SidebarContent>
        {visibleSections.map((section) => (
          <SidebarGroup key={section.title}>
            <SidebarGroupLabel className="text-xs font-semibold tracking-wider uppercase">
              {section.title}
            </SidebarGroupLabel>
            <SidebarMenu>
              {section.items.map((item) => {
                const href = item.href(locale)
                const active = isActive(href)
                return (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={item.label}
                    >
                      <Link href={href}>
                        <item.icon className="h-5 w-5 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  )
}
